import { ArrowBack, LibraryAddRounded } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { useState } from "react";
import Stepper from "../../../components/stepper/stepper";
import MainLayout from "../../../layouts/main/main_layout";
import useUser from "../../../providers/user/user_provider";
import {
  SchemeDetailsInputs,
  SchemeEligibilityInputs,
  SchemeOtherDetailsInputs,
} from "../models/scheme_form";
import { SchemeProvider } from "../providers/scheme_provider";
import OtherSchemeInformation from "./components/other_scheme_information";
import SchemeDetails from "./components/scheme_details";
import SchemeEligibilityCriteria from "./components/scheme_eligibility_criteria";
import { addScheme } from "../../../services/schemes/schemes";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { useNavigate } from "react-router-dom";
import { queryClient } from "../../../providers/query/query";
import { black, greyEnabled, purple, purpleLight } from "../../../theme/colors";
import { DEFAULT_ORG_ID } from "../../../constants";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import OrgSelector from "../../../components/org_selector/org_selector";

export default function AddNewSchemes() {
  const { hasPermission, userPermissions } = useUser();
  const { selectedOrgId } = useSelectedOrgStore();
  // A Platform Admin picking an org below creates the scheme there;
  // otherwise falls back to the caller's own org, matching create_user.tsx.
  const targetOrgId = selectedOrgId ?? userPermissions?.orgId ?? DEFAULT_ORG_ID;
  const { showSnackbar } = useFeedback();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [schemeDetails, setSchemeDetails] = useState<SchemeDetailsInputs>({
    schemeName: "",
    governmentTypeId: null,
    typeOfCategory: [],
    topScheme: false,
    providerId: null,
  });
  const [otherDetails, setOtherDetails] = useState<SchemeOtherDetailsInputs>({
    applicationProcess: null,
    applicationProcessDetails: "",
    benefits: "",
    details: "",
    documentRequired: "",
    eligibility: "",
    exclusions: "",
    summary: "",
  });
  const [eligibilityDetails, setEligibilityDetails] =
    useState<SchemeEligibilityInputs>({
      bplCategory: null,
      differentlyAbled: null,
      fromAge: 1,
      toAge: 120,
      genders: [],
      caste: [],
      maritalStatus: [],
      minority: null,
      occupation: [],
      residence: [],
      state: [],
      student: null,
      workingStatus: [],
      familyIncome: [],
    });

  async function submit(eligibilityDetails: SchemeEligibilityInputs) {
    setIsLoading(true);
    await addScheme(schemeDetails, otherDetails, eligibilityDetails, targetOrgId)
      .then((res) => {
        if (res.status === 200) {
          showSnackbar("Scheme added successfully");
          queryClient.invalidateQueries({ queryKey: ["schemes"] });
          navigate("/schemes");
        } else {
          showSnackbar("Something went wrong while adding the scheme.");
        }
      })
      .catch((e) => {
        if (e.response?.status === 403) {
          showSnackbar("You do not have permission to perform this operation.");
        } else if (e.response?.status === 400) {
          showSnackbar("Scheme already exists.");
        } else {
          showSnackbar(
            e?.response?.data?.error?.message ||
              "Something went wrong while adding the scheme."
          );
        }
      })
      .finally(() => setIsLoading(false));
  }
  return (
    <MainLayout>
      <SchemeProvider>
        <Grid container spacing={3}>
          <Grid item md={12} sm={12} xs={12} lg={10} xl={10}>
            {/* {hasPermission("Schemes", "Read") ? ( */}
            <Card
              elevation={0}
              sx={{ width: "100%", borderRadius: 3, border: "1px solid #ECECEC", m: 1 }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <IconButton onClick={() => navigate(-1)}>
                    <ArrowBack />
                  </IconButton>
                  <Box display="flex" alignItems="center" gap={1.5} ml={0.5}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: purpleLight,
                        flexShrink: 0,
                      }}
                    >
                      <LibraryAddRounded sx={{ color: purple, fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color={black}>
                        Add New Scheme
                      </Typography>
                      <Typography variant="body2" color={greyEnabled}>
                        Create a scheme with its details and eligibility criteria.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box mt={2} maxWidth={360}>
                  <OrgSelector label="Organisation" fullWidth lockToOwnOrg platformDefaultsToOwnOrg />
                </Box>
                <Divider sx={{ mt: 2, mb: 2 }} />
                <Box overflow="auto" flex={1} position="relative">
                  <Box pt={2} pb={2}>
                    <Stepper
                      activeStep={activeStep}
                      steps={[
                        "Scheme Details",
                        "Other Scheme Information",
                        "Scheme Eligibility Criteria",
                      ]}
                    />
                  </Box>
                  {activeStep === 0 && (
                    <SchemeDetails
                      schemeDetails={schemeDetails}
                      onSubmit={(data: SchemeDetailsInputs) => {
                        setSchemeDetails(data);
                        setActiveStep(1);
                      }}
                    />
                  )}
                  {activeStep === 1 && (
                    <OtherSchemeInformation
                      otherDetails={otherDetails}
                      onSubmit={(data: SchemeOtherDetailsInputs) => {
                        setOtherDetails(data);
                        setActiveStep(2);
                      }}
                      goBack={() => setActiveStep(0)}
                    />
                  )}
                  {activeStep === 2 && (
                    <SchemeEligibilityCriteria
                      onSubmit={async (data) => {
                        setEligibilityDetails(data);
                        submit(data);
                      }}
                      eligibilityDetails={eligibilityDetails}
                      goBack={() => setActiveStep(1)}
                      submitting={isLoading}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
            {/* ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={600}
            >
              <AccessDenied />
            </Box>
          )} */}
          </Grid>
        </Grid>
      </SchemeProvider>
    </MainLayout>
  );
}
